import React, { useState, useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";
import DropdownWithCustomValuesModal from "../../components/DropdownWithCustomValuesModal";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullScreenLoader from "../../components/FullScreenLoader";
import AskConfirmationModal from "../../components/modals/AskConfirmationModal";
import ActivityApproveRejectConfirmationModal from "../../components/modals/ActivityApproveRejectConfirmationModal";
import PupilActivityEnrollmentModal from "./PupilActivityEnrollmentModal";

function EventCreationForm() {
  const token = localStorage.getItem("token");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");
  const userId = localStorage.getItem("userId");
  const roleId = localStorage.getItem("roleId");

  const formViewOnlyUserRole = ["9", "10", "19", "25"].includes(roleId);

  const [triggerRerender, setTriggerRerender] = useState(false);


  const [centerId, setCenterId] = useState(savedCenterId || null);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [teachersData, setTeachersData] = useState([]);

  const [eventFileNameState, setEventFileNameState] = useState("");
  const [termsFileNameState, setTermsFileNameState] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);

  const [
    isCustomDropdownValuesModalVisible,
    setIsCustomDropdownValuesModalVisible,
  ] = useState(false);
  const [customDropdownName, setCustomDropdownName] = useState("");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
    setError,
  } = useForm({
    defaultValues: {
      id: 0,
      eventName: "",
      description: "",
      eventType: null,
      targetAudience: null,
      speakerInfo: "",
      maxAttendees: "",
      minAttendees: "",
      activityVenueId: null,
      agenda: "",
      feesPerAttendee: "",
      registrationOpenDate: null,
      registrationCloseDates: null,
      eventFileName: "",
      eventUploadPath: "",
      eventImageBase64String: "",
      termsText: "",
      termsFileName: "",
      termsUploadPath: "",
      termsFileBase64String: "",
      internalNotes: "",
      rsvp: "",
      contactName: "",
      email: "",
      phone: "",
      contractId: null,
      providerType: null,
      revenueShare: null,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      campusActivityEventDatesDtos: [
        {
          id: 0,
          eventDate: null,
          startTime: "",
          endTime: "",
        },
      ],
    },
  });

  const resetForm = () => {
    setTriggerRerender((prev) => !prev);
    setPreviewUrl(null);
    setTermsFileNameState("");
    reset({
      id: 0,
      eventName: "",
      description: "",
      eventType: null,
      targetAudience: null,
      speakerInfo: "",
      maxAttendees: "",
      minAttendees: "",
      activityVenueId: null,
      agenda: "",
      feesPerAttendee: "",
      registrationOpenDate: null,
      registrationCloseDates: null,
      eventFileName: "",
      eventUploadPath: "",
      eventImageBase64String: "",
      termsText: "",
      termsFileName: "",
      termsUploadPath: "",
      termsFileBase64String: "",
      internalNotes: "",
      rsvp: "",
      contactName: "",
      email: "",
      phone: "",
      contractId: null,
      providerType: null,
      revenueShare: null,
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      campusActivityEventDatesDtos: [
        {
          id: 0,
          eventDate: null,
          startTime: "",
          endTime: "",
        },
      ],
    });
  };

  const [isConfimationModalOpen, setIsConfimationModalOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [isConfirmApproveRejectModalOpen, setIsConfirmApproveRejectModalOpen] =
    useState(false);

  const [activityVenueData, setActivityVenueData] = useState([]);
  const [eventTypeData, setEventTypeData] = useState([]);
  const [targetAudienceData, setTargetAudienceData] = useState([]);

  const [providerTypeData, setProviderTypeData] = useState([]);
  const [contractsData, setContractsData] = useState([]);

  const [loading, setLoading] = useState(false);

  const [dayAndTimeSlotsData, setDayAndTimeSlotsData] = useState([]);
  const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);

  const fetchCommonSettings = async (moduleName, setData) => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=${moduleName}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setData(response?.commonSettingDetail);
    } catch (error) {
      console.error(`Error fetching ${moduleName}:`, error);
    }
  };

  useEffect(() => {
    fetchCommonSettings("activityVenueId", setActivityVenueData);
    fetchCommonSettings("eventType", setEventTypeData);
    fetchCommonSettings("targetAudience", setTargetAudienceData);
    fetchCommonSettings("providerType", setProviderTypeData);
  }, [token]);

  const selectedProviderType = watch("providerType");

  useEffect(() => {
    if (selectedProviderType && selectedProviderType.value === "Internal") {
      setValue("revenueShare", null);
    }
  }, [selectedProviderType, setValue]);

  const [eventActivitiesData, setEventActivitiesData] = useState([]);

  const getEventActivitiesList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(
        `CampusActivity/GetAllActivityEvent?roleId=${roleId}&centerId=${savedCenterId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setEventActivitiesData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const getUsersByCenterIdAndRoleId = async () => {
    try {
      const response = await get(
        `UserAuthentication/GetUsersByCenterIdAndRoleId?centerId=${centerId}&roleId=${7}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTeachersData(response);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const getContractsList = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    try {
      const response = await get(`CampusContract/GetAllContracts`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setContractsData(response);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    getEventActivitiesList();
    getUsersByCenterIdAndRoleId();
    getContractsList();
  }, [triggerRerender]);

  const handleEdit = async (activityId) => {
    try {
      const response = await get(
        `CampusActivity/GetActivityEventById?id=${activityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const activity = response;

      const eventDates = activity.campusActivityEventDatesDtos.map((item) => ({
        ...item,
        eventDate: item.eventDate.split("T")[0],
      }));

      const selectedContract = contractsData.find(
        (contract) => contract.id === activity.contractId
      );

      const imageUrl = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${activity.eventUploadPath}`;

      setPreviewUrl(imageUrl);
      setEventFileNameState(activity.eventFileName);
      setTermsFileNameState(activity.termsFileName);

      reset({
        ...activity,
        contractId: {
          value: selectedContract?.id || "",
          label: selectedContract?.name || "",
        },
        registrationOpenDate: activity.registrationOpenDate.split("T")[0],
        registrationCloseDates: activity.registrationCloseDates.split("T")[0],
        campusActivityEventDatesDtos: eventDates,
        updatedBy: localStorage.getItem("userId"),
      });
    } catch (error) {
      console.error("Error editing section:", error);
    }
  };

  const handleDelete = async (activityId) => {
    setSelectedActivityId(activityId);
    setIsConfimationModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsConfimationModalOpen(false);
    setSelectedActivityId(null);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await post(
        `CampusActivity/DeleteActivityEvent?id=${selectedActivityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        resetForm();
      } else {
        toast.error(response.message || "An error occurred while deleting.");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setIsConfimationModalOpen(false);
      setSelectedActivityId(null);
    }
  };

  const handleActivityApproveOrReject = (activityId) => {
    setSelectedActivityId(activityId);
    setIsConfirmApproveRejectModalOpen(true);
  };

  const handleCloseActivityApproveRejectModal = () => {
    setIsConfirmApproveRejectModalOpen(false);
    setSelectedActivityId(null);
  };

  const handleConfirmApprove = async (
    roleId,
    activityId,
    approvalStatus,
    remarks
  ) => {
    let apiEndpoint;
    let formData = {
      id: activityId,
      activityName: "Event",
    };

    if (roleId === "25") {
      // COO
      apiEndpoint = "CampusActivity/CampusActivityApproval";
      formData.approvalStatus = approvalStatus;
      formData.remarks = remarks;
    } else if (roleId === "19") {
      // Accounts
      apiEndpoint = "CampusActivity/CampusActivityApprovalByAccount";
      formData.accountsApprovalStatus = approvalStatus;
      formData.accountsRemarks = remarks;
    } else {
      console.log("Error!");
      return;
    }

    setLoading(true);

    try {
      const response = await post(apiEndpoint, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.isSuccess) {
        toast.success("Success!");
        resetForm();
      } else {
        toast.error(response.message || "An error occurred.");
      }
    } catch (error) {
      console.error("Error approving/rejecting activity:", error);
    } finally {
      setIsConfirmApproveRejectModalOpen(false);
      setSelectedActivityId(null);
      setLoading(false);
    }
  };

  const handlePublishActivity = async (activityId) => {
    const formData = {
      id: activityId,
      activityName: "Event",
    };

    try {
      const response = await post(
        `CampusActivity/CampusActivityPublished`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success(response.message || "Success!");
        resetForm();
      } else {
        toast.error(response.message || "An error occurred.");
      }
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setIsConfirmApproveRejectModalOpen(false);
      setSelectedActivityId(null);
    }
  };

  const handleEnroll = async (activityId) => {
    setSelectedActivityId(activityId);

    try {
      const response = await get(
        `CampusActivity/GetActivityEventById?id=${activityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const activity = response;

      const slots = activity.campusActivityEventDatesDtos;

      setDayAndTimeSlotsData(slots);
      setIsEnrollModalOpen(true);

      console.log("Slots:", slots);
    } catch (error) {
      console.error("Error editing section:", error);
    }
  };

  const handleCloseEnrollModal = () => {
    setIsEnrollModalOpen(false);
    setSelectedActivityId(null);
  };

  const PERMISSIONS = {
    PARENT: {
      roleIds: ["9", "10"],
      actions: [
        {
          name: "view",
          icon: "fas fa-eye",
          handler: "handleEdit",
          tooltip: "View",
        },
        {
          name: "enroll",
          icon: "fas fa-user-plus",
          handler: "handleEnroll",
          tooltip: "Enroll",
        },
      ],
    },
    ACCOUNTS: {
      roleIds: ["19"],
      actions: [
        {
          name: "view",
          icon: "fas fa-eye",
          handler: "handleEdit",
          tooltip: "View",
        },
        {
          name: "approveReject",
          icon: "fas fa-question",
          handler: "handleActivityApproveOrReject",
          tooltip: "Approve/Reject",
          disabled: (row) =>
            ["Approved", "Rejected", "Published"].includes(row.status) ||
            ["Approved", "Rejected"].includes(row.accountsApprovalStatus),
        },
      ],
    },
    COO: {
      roleIds: ["25"],
      actions: [
        {
          name: "view",
          icon: "fas fa-eye",
          handler: "handleEdit",
          tooltip: "View",
        },
        {
          name: "approveReject",
          icon: "fas fa-question",
          handler: "handleActivityApproveOrReject",
          tooltip: "Approve/Reject",
          disabled: (row) =>
            ["Approved", "Rejected", "Published"].includes(row.status) ||
            row.cooApprovalStatus !== null,
        },
      ],
    },
    DEFAULT: {
      roleIds: [],
      actions: [
        {
          name: "edit",
          icon: "fas fa-pencil",
          handler: "handleEdit",
          tooltip: "Edit",
          disabled: (row) =>
            ["Approved", "Rejected"].includes(row.accountsApprovalStatus),
        },
        {
          name: "delete",
          icon: "fas fa-trash",
          handler: "handleDelete",
          tooltip: "Delete",
          disabled: (row) =>
            ["Approved", "Rejected"].includes(row.accountsApprovalStatus),
        },
        {
          name: "publish",
          icon: "fas fa-check-double",
          handler: "handlePublishActivity",
          tooltip: "Publish",
          visible: (row) =>
            row.isPublished === null &&
            row.accountsApprovalStatus === "Approved" &&
            row.cooApprovalStatus === "Approved",
        },
      ],
    },
  };

  const getActionsForRole = (roleId) => {
    const roleConfig = Object.values(PERMISSIONS).find(
      (role) => role.roleIds.length === 0 || role.roleIds.includes(roleId)
    );
    return roleConfig ? roleConfig.actions : [];
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "eventName",
        header: "Event Name",
        // size: 150,
      },
      {
        accessorKey: "registrationOpenDate",
        header: "Registration Open",
        size: 200,
        Cell: ({ cell }) => cell.getValue()?.split("T")[0],
      },
      {
        accessorKey: "registrationCloseDates",
        header: "Registration Close",
        size: 200,
        Cell: ({ cell }) => cell.getValue()?.split("T")[0],
      },
      {
        accessorKey: "status",
        header: "Activity Status",
        // size: 150,
      },
      {
        accessorKey: "accountsApprovalStatus",
        header: "Accounts Approval",
        size: 200,
      },
      {
        accessorKey: "cooApprovalStatus",
        header: "COO Approval",
        size: 200,
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => {
            const actions = getActionsForRole(roleId);
            const handlers = {
                handleEdit,
                handleActivityApproveOrReject,
                handleEnroll,
                handleDelete,
                handlePublishActivity,
            };

            return (
                <div className="d-flex gap-1">
                  {actions.map((action) => {
                    if (action.visible && !action.visible(row.original)) {
                      return null;
                    }

                    return (
                      <button
                        key={action.name}
                        type="button"
                        className={`btn p-0 border-0 custom-disabled-btn`}
                        data-tooltip-id="global-tooltip"
                        data-tooltip-content={action.tooltip}
                        onClick={() => handlers[action.handler](row.original.id)}
                        disabled={action.disabled && action.disabled(row.original)}
                      >
                        <i className={action.icon}></i>
                      </button>
                    );
                  })}
                </div>
            );
        },
      },
    ],
    [roleId, eventActivitiesData]
  );

  const exportToExcel = () => {
    const filteredData = eventActivitiesData.map((row, index) => ({
      "Sr No": index + 1,
      "Event Name": row.eventName,
      "Registration Open": row.registrationOpenDate?.split("T")[0],
      "Registration Close": row.registrationCloseDates?.split("T")[0],
      "Activity Status": row.status,
      "Accounts Approval": row.accountsApprovalStatus,
      "COO Approval": row.cooApprovalStatus,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Events");
    XLSX.writeFile(workbook, "Events.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleIdLoggedIn = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleIdLoggedIn == "2" ? orgLogo : roleIdLoggedIn == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Events List";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = eventActivitiesData.map((row, index) => {
      return [
        index + 1,
        row.eventName,
        row.registrationOpenDate?.split("T")[0],
        row.registrationCloseDates?.split("T")[0],
        row.status,
        row.accountsApprovalStatus,
        row.cooApprovalStatus,
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("EventsList.pdf");
  };

  const fetchClassSectionsByCenterId = async () => {
    try {
      if (centerId) {
        const response = await get(
          `ClassSection/GetAllClassSectionByCenterId?centerId=${centerId}&userId=${userId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setClassSectionsData(response);
      } else {
        setClassSectionsData([]);
      }
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  useEffect(() => {
    fetchClassSectionsByCenterId();
  }, []);

  const handleCustomDropdownSetting = (dropdownName) => {
    setCustomDropdownName(dropdownName);
    setIsCustomDropdownValuesModalVisible(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setEventFileNameState(file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue("eventImageBase64String", base64String, {
          shouldValidate: true,
        });
        setPreviewUrl(reader.result);
        trigger("eventFileName");
      };
      reader.readAsDataURL(file);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleTermsFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setTermsFileNameState(file.name);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(",")[1];
        setValue("termsFileBase64String", base64String, {
          shouldValidate: true,
        });
        trigger("termsFileName");
      };
      reader.readAsDataURL(file);
    } else {
      setTermsFileNameState("");
    }
  };

  const { fields, append, remove } = useFieldArray({
    control,
    name: "campusActivityEventDatesDtos",
  });

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const formData = {
      ...data,
      eventFileName: eventFileNameState,
      termsFileName: termsFileNameState,
      contractId: data?.contractId?.value,
      centerId: savedCenterId,
    };

    // console.log(formData);
    // return;

    try {
      const response = await post(
        "CampusActivity/AddUpdateActivityEvent",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        toast.success("Success!");
        resetForm();
      } else {
        toast.error(response.message || "An unexpected error occurred");
      }
    } catch (error) {
      console.error("Error.", error);
    }
  };

  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showActivityDetailsModal, setShowActivityDetailsModal] =
    useState(false);

  const fetchEvents = (fetchInfo, successCallback, failureCallback) => {
    get(
      `CampusActivity/GetAllActivityEvent?roleId=${roleId}&centerId=${savedCenterId}`
    )
      .then((response) => {
        if (Array.isArray(response)) {
          const allEvents = response.map((activity) => {
            const eventDateTime = new Date(activity.registrationOpenDate);
            // const endDateTime = new Date(activity.endDate);

            // endDateTime.setDate(endDateTime.getDate() + 1);

            return {
              id: activity.id,
              title: activity.eventName,
              start: eventDateTime.toISOString(),
              // end: endDateTime.toISOString(),
              extendedProps: {
                eventName: activity.eventName,
                description: activity.description,
                maxStudents: activity.maxStudents,
                minStudents: activity.minStudents,
                feesPerStudent: activity.feesPerStudent,
                registrationOpenDate: activity.registrationOpenDate,
                registrationClose: activity.registrationClose,
                materialsRequired: activity.materialsRequired,
                contactName: activity.contactName,
                email: activity.email,
                phone: activity.phone,
              },
            };
          });

          successCallback(allEvents);
        } else {
          failureCallback(new Error("Unexpected response format"));
        }
      })
      .catch((error) => {
        console.error("Error fetching events:", error);
        failureCallback(error);
      });
  };

  const handleEventClick = (clickInfo) => {
    const { event } = clickInfo;

    setSelectedEvent({
      eventName: event.extendedProps.eventName,
      description: event.extendedProps.description,
      maxStudents: event.extendedProps.maxStudents,
      minStudents: event.extendedProps.minStudents,
      feesPerStudent: event.extendedProps.feesPerStudent,
      registrationOpenDate: event.extendedProps.registrationOpenDate,
      registrationClose: event.extendedProps.registrationClose,
      materialsRequired: event.extendedProps.materialsRequired,
      contactName: event.extendedProps.contactName,
      email: event.extendedProps.email,
      phone: event.extendedProps.phone,
      eventDate: event.eventDate,
      endDate: event.endDate,
    });

    setShowActivityDetailsModal(true);
  };

  const handleCloseActivityDetailsModal = () => {
    setShowActivityDetailsModal(false);
    setSelectedEvent(null);
  };

  return (
    <>
      <div className="row g-3">
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-body">
              <h4 className="mb-3 fs-8">Event</h4>
              <div
                className={`position-relative ${
                  formViewOnlyUserRole ? "p-3" : ""
                }`}
                style={formViewOnlyUserRole ? { pointerEvents: "none" } : {}}
              >
                {formViewOnlyUserRole && (
                  <div
                    className="p-3 form-overlay position-absolute top-0 start-0 w-100 h-100"
                    style={{
                      backgroundColor: "rgba(0, 0, 0, 0.03)",
                      zIndex: 10,
                    }}
                  ></div>
                )}
                <form
                  className="row g-3 new-form-style"
                  onSubmit={handleSubmit(saveData)}
                >
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Event Name<span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register(`eventName`, {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.eventName && (
                      <small className="text-danger">
                        {errors.eventName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Speaker Info
                    </label>
                    <input
                      {...register(`speakerInfo`)}
                      type="text"
                      className="form-control"
                    />
                    {errors.speakerInfo && (
                      <small className="text-danger">
                        {errors.speakerInfo.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Event Type<span className="text-danger fs-9">*</span>
                    </label>
                    <div className="d-flex gap-2">
                      <div className="w-100">
                        <Controller
                          name="eventType"
                          control={control}
                          rules={{ required: "This field is required." }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              className="react-select-style"
                              classNamePrefix="react-select-style-pref"
                              options={eventTypeData.map((item) => ({
                                value: item.id,
                                label: item.value,
                              }))}
                              isClearable
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={
                                eventTypeData
                                  .map((item) => ({
                                    value: item.id,
                                    label: item.value,
                                  }))
                                  .find(
                                    (option) => option.value === field.value
                                  ) || null
                              }
                            />
                          )}
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          style={{
                            padding: "5.5px 10px",
                          }}
                          onClick={() =>
                            handleCustomDropdownSetting("eventType")
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                    {errors.eventType && (
                      <small className="text-danger">
                        {errors.eventType.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Target Audience<span className="text-danger fs-9">*</span>
                    </label>
                    <div className="d-flex gap-2">
                      <div className="w-100">
                        <Controller
                          name="targetAudience"
                          control={control}
                          rules={{ required: "This field is required." }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              className="react-select-style"
                              classNamePrefix="react-select-style-pref"
                              options={targetAudienceData.map((item) => ({
                                value: item.id,
                                label: item.value,
                              }))}
                              isClearable
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={
                                targetAudienceData
                                  .map((item) => ({
                                    value: item.id,
                                    label: item.value,
                                  }))
                                  .find(
                                    (option) => option.value === field.value
                                  ) || null
                              }
                            />
                          )}
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          style={{
                            padding: "5.5px 10px",
                          }}
                          onClick={() =>
                            handleCustomDropdownSetting("targetAudience")
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                    {errors.targetAudience && (
                      <small className="text-danger">
                        {errors.targetAudience.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Venue/Location<span className="text-danger fs-9">*</span>
                    </label>
                    <div className="d-flex gap-2">
                      <div className="w-100">
                        <Controller
                          name="activityVenueId"
                          control={control}
                          rules={{ required: "This field is required." }}
                          render={({ field }) => (
                            <Select
                              {...field}
                              className="react-select-style"
                              classNamePrefix="react-select-style-pref"
                              options={activityVenueData.map((item) => ({
                                value: item.id,
                                label: item.value,
                              }))}
                              isClearable
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={
                                activityVenueData
                                  .map((item) => ({
                                    value: item.id,
                                    label: item.value,
                                  }))
                                  .find(
                                    (option) => option.value === field.value
                                  ) || null
                              }
                            />
                          )}
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          className="btn btn-outline-primary"
                          style={{
                            padding: "5.5px 10px",
                          }}
                          onClick={() =>
                            handleCustomDropdownSetting("activityVenueId")
                          }
                        >
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                    {errors.activityVenueId && (
                      <small className="text-danger">
                        {errors.activityVenueId.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Minimum Attendees
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("minAttendees", {
                        valueAsNumber: true,
                        required: "This field is required.",
                        validate: (value) =>
                          value >= 0 || "Please enter a correct value.",
                      })}
                      type="number"
                      className="form-control"
                      onKeyDown={(e) => {
                        if (["-", "e", "+", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e) => {
                        e.target.value =
                          e.target.value < 0 ? 1 : e.target.value;
                      }}
                    />
                    {errors.minAttendees && (
                      <small className="text-danger">
                        {errors.minAttendees.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Maximum Attendees
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("maxAttendees", {
                        valueAsNumber: true,
                        required: "This field is required.",
                        validate: (value) =>
                          value >= 0 || "Please enter a correct value.",
                      })}
                      type="number"
                      className="form-control"
                      onKeyDown={(e) => {
                        if (["-", "e", "+", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e) => {
                        e.target.value =
                          e.target.value < 0 ? 1 : e.target.value;
                      }}
                    />
                    {errors.maxAttendees && (
                      <small className="text-danger">
                        {errors.maxAttendees.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Fees per Attendee (SAR)
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("feesPerAttendee", {
                        valueAsNumber: true,
                        required: "This field is required.",
                        validate: (value) =>
                          value >= 0 || "Please enter a correct value.",
                      })}
                      type="number"
                      className="form-control"
                      onKeyDown={(e) => {
                        if (["-", "e", "+", "."].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onInput={(e) => {
                        e.target.value =
                          e.target.value < 0 ? 1 : e.target.value;
                      }}
                    />
                    {errors.feesPerAttendee && (
                      <small className="text-danger">
                        {errors.feesPerAttendee.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Contact Name
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("contactName", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.contactName && (
                      <small className="text-danger">
                        {errors.contactName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Contact Email
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("email", {
                        required: "This field is required.",
                        pattern: {
                          value:
                            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                          message: "Please enter a valid email.",
                        },
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.email && (
                      <small className="text-danger">
                        {errors.email.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Contact Phone
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("phone", {
                        required: "This field is required.",
                      })}
                      type="text"
                      className="form-control"
                    />
                    {errors.phone && (
                      <small className="text-danger">
                        {errors.phone.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Contract
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="contractId"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={contractsData.map((contract) => ({
                            value: contract.id,
                            label: contract.name,
                          }))}
                          onChange={(selectedOption, actionMeta) => {
                            if (actionMeta.action === "clear") {
                              setValue("contractId", null);
                            } else {
                              setValue("contractId", selectedOption);
                            }
                          }}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                        />
                      )}
                    />
                    {errors.contractId && (
                      <small className="text-danger">
                        {errors.contractId.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-4">
                    <label className="form-label form-label-new">
                      Provider Type<span className="text-danger fs-9">*</span>
                    </label>
                    <Controller
                      name="providerType"
                      control={control}
                      rules={{ required: "This field is required." }}
                      render={({ field }) => (
                        <Select
                          {...field}
                          className="react-select-style"
                          classNamePrefix="react-select-style-pref"
                          options={providerTypeData.map((item) => ({
                            value: item.id,
                            label: item.value,
                          }))}
                          onChange={(selectedOption) =>
                            field.onChange(
                              selectedOption ? selectedOption.value : ""
                            )
                          }
                          value={
                            providerTypeData
                              .map((item) => ({
                                value: item.id,
                                label: item.value,
                              }))
                              .find((option) => option.value === field.value) ||
                            null
                          }
                        />
                      )}
                    />
                    {errors.providerType && (
                      <small className="text-danger">
                        {errors.providerType.message}
                      </small>
                    )}
                  </div>
                  {providerTypeData.find(
                    (item) => item.id === selectedProviderType
                  )?.value === "External" && (
                    <div className="col-md-4">
                      <label className="form-label form-label-new">
                        Revenue Share<span className="text-danger fs-9">*</span>
                      </label>
                      <input
                        {...register("revenueShare", {
                          valueAsNumber: true,
                          required: "This field is required.",
                          validate: (value) =>
                            value >= 0 || "Please enter correct value.",
                        })}
                        type="number"
                        className="form-control"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onInput={(e) => {
                          e.target.value =
                            e.target.value < 0 ? 1 : e.target.value;
                        }}
                      />
                      {errors.revenueShare && (
                        <small className="text-danger">
                          {errors.revenueShare.message}
                        </small>
                      )}
                    </div>
                  )}

                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Event Image/Banner
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <div className="image-uploader-wrapper-2 d-flex flex-column align-items-center">
                      <div className="image-preview d-flex justify-content-center align-items-center flex-shrink-0 mb-2 mt-1">
                        {previewUrl ? (
                          <img src={previewUrl} alt="Preview" />
                        ) : (
                          <div>
                            <span className="fas fa-image fs-3 text-light"></span>
                          </div>
                        )}
                      </div>
                      <input
                        {...register("eventFileName", {
                          required: !previewUrl
                            ? "This field is required."
                            : false,
                        })}
                        type="file"
                        className="form-control"
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </div>
                    {errors.eventFileName && (
                      <small className="text-danger">
                        {errors.eventFileName.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Description<span className="text-danger fs-9">*</span>
                    </label>
                    <textarea
                      {...register("description", {
                        required: "This field is required.",
                      })}
                      className="form-control"
                    ></textarea>
                    {errors.description && (
                      <small className="text-danger">
                        {errors.description.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Registration Open
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("registrationOpenDate", {
                        required: "This field is required.",
                      })}
                      type="date"
                      className="form-control"
                    />
                    {errors.registrationOpenDate && (
                      <small className="text-danger">
                        {errors.registrationOpenDate.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Registration Close
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <input
                      {...register("registrationCloseDates", {
                        required: "This field is required.",
                        validate: (value) =>
                          !watch("registrationOpenDate") ||
                          new Date(value) >=
                            new Date(watch("registrationOpenDate")) ||
                          "Must be after Open Date.",
                      })}
                      type="date"
                      className="form-control"
                    />
                    {errors.registrationCloseDates && (
                      <small className="text-danger">
                        {errors.registrationCloseDates.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      RSVP<span className="text-danger fs-9">*</span>
                    </label>
                    <textarea
                      {...register("rsvp", {
                        required: "This field is required.",
                      })}
                      className="form-control"
                    ></textarea>
                    {errors.rsvp && (
                      <small className="text-danger">
                        {errors.rsvp.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Agenda/Schedule
                    </label>
                    <textarea
                      {...register("agenda")}
                      className="form-control"
                    ></textarea>
                    {errors.agenda && (
                      <small className="text-danger">
                        {errors.agenda.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Internal Notes
                    </label>
                    <textarea
                      {...register("internalNotes")}
                      className="form-control"
                    ></textarea>
                    {errors.internalNotes && (
                      <small className="text-danger">
                        {errors.internalNotes.message}
                      </small>
                    )}
                  </div>

                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Terms/Rules
                    </label>
                    <textarea
                      {...register("termsText")}
                      className="form-control"
                    ></textarea>
                    {errors.termsText && (
                      <small className="text-danger">
                        {errors.termsText.message}
                      </small>
                    )}
                  </div>
                  <div className="col-md-6">
                    <label className="form-label form-label-new">
                      Terms/Rules File
                    </label>
                    <input
                      {...register("termsFileName")}
                      type="file"
                      className="form-control"
                      accept=".txt"
                      onChange={handleTermsFileChange}
                    />
                    {termsFileNameState && (
                      <div className="">
                        <small className="text-muted">
                          {termsFileNameState}
                        </small>
                      </div>
                    )}
                    {errors.termsFileName && (
                      <small className="text-danger">
                        {errors.termsFileName.message}
                      </small>
                    )}
                  </div>

                  <div className="col-12">
                    <div className="row row-cols-4 gy-3 gx-2">
                      <div className="col">
                        <label className="form-label">
                          Event Date <span className="text-danger fs-9">*</span>
                        </label>
                      </div>
                      <div className="col">
                        <label className="form-label">
                          Start Time <span className="text-danger fs-9">*</span>
                        </label>
                      </div>
                      <div className="col">
                        <label className="form-label">
                          End Time <span className="text-danger fs-9">*</span>
                        </label>
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Action</label>
                      </div>
                    </div>

                    {fields.map((field, index) => {
                      return (
                        <div
                          className="row row-cols-4 gy-3 gx-2 mb-2"
                          key={field.id}
                        >
                          <div className="col">
                            <input
                              type="date"
                              className="form-control"
                              {...register(
                                `campusActivityEventDatesDtos.${index}.eventDate`,
                                {
                                  required: "This field is required.",
                                }
                              )}
                            />
                            {errors?.campusActivityEventDatesDtos?.[index]
                              ?.eventDate && (
                              <small className="text-danger">
                                {
                                  errors.campusActivityEventDatesDtos[index]
                                    .eventDate.message
                                }
                              </small>
                            )}
                          </div>
                          <div className="col">
                            <input
                              type="time"
                              className="form-control"
                              {...register(
                                `campusActivityEventDatesDtos.${index}.startTime`,
                                {
                                  required: "This field is required.",
                                }
                              )}
                            />
                            {errors?.campusActivityEventDatesDtos?.[index]
                              ?.startTime && (
                              <small className="text-danger">
                                {
                                  errors.campusActivityEventDatesDtos[index]
                                    .startTime.message
                                }
                              </small>
                            )}
                          </div>
                          <div className="col">
                            <input
                              type="time"
                              className="form-control"
                              {...register(
                                `campusActivityEventDatesDtos.${index}.endTime`,
                                {
                                  required: "This field is required.",
                                  validate: (value) => {
                                    const startTime = watch(
                                      `campusActivityEventDatesDtos.${index}.startTime`
                                    );
                                    if (!startTime) return true;
                                    return (
                                      value >= startTime ||
                                      "Must be same or after Start Time."
                                    );
                                  },
                                }
                              )}
                            />
                            {errors?.campusActivityEventDatesDtos?.[index]
                              ?.endTime && (
                              <small className="text-danger">
                                {
                                  errors.campusActivityEventDatesDtos[index]
                                    .endTime.message
                                }
                              </small>
                            )}
                          </div>
                          <div className="col-md-2 d-flex align-items-start">
                            <button
                              className="btn btn-outline-primary py-1 px-2 me-1"
                              type="button"
                              onClick={() =>
                                append({
                                  id: 0,
                                  eventDate: null,
                                  startTime: "",
                                  slotEndTime: "",
                                })
                              }
                            >
                              <span className="fas fa-plus"></span>
                            </button>
                            {fields.length > 1 && (
                              <button
                                className="btn btn-outline-danger py-1 px-2"
                                type="button"
                                onClick={() => remove(index)}
                              >
                                <span className="fas fa-minus"></span>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {["9", "10", "19", "25"].includes(roleId) ? null : (
                    <div className="col-12 mt-2">
                      <button
                        className="btn btn-primary btn-sm"
                        type="submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Save" : "Save"}
                      </button>
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="calendar-container h-100">
            <FullCalendar
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              events={fetchEvents}
              // eventClick={handleEventClick}
              dayMaxEvents={1}
              height="auto"
              eventDisplay="block"
              headerToolbar={{
                left: "prev,next today",
                center: "title",
                right: "dayGridMonth",
              }}
              eventTimeFormat={{
                hour: "2-digit",
                minute: "2-digit",
                meridiem: true,
              }}
            />
          </div>
        </div>

        <div className="col-md-12">
          <div className="card h-100">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={eventActivitiesData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                    columnPinning: { right: ["actions"] },
                  }}
              />
            </div>
          </div>
        </div>
      </div>

      {isCustomDropdownValuesModalVisible && (
        <DropdownWithCustomValuesModal
          dropdownName={customDropdownName}
          onClose={() => {
            setIsCustomDropdownValuesModalVisible(false);
            if (customDropdownName === "activityVenueId") {
              fetchCommonSettings("activityVenueId", setActivityVenueData);
            } else if (customDropdownName === "eventType") {
              fetchCommonSettings("eventType", setEventTypeData);
            } else if (customDropdownName === "targetAudience") {
              fetchCommonSettings("targetAudience", setTargetAudienceData);
            } else if (customDropdownName === "providerType") {
              fetchCommonSettings("providerType", setProviderTypeData);
            }
          }}
        />
      )}

      {isConfimationModalOpen && (
        <AskConfirmationModal
          handleClose={handleCloseModal}
          handleConfirm={handleConfirmDelete}
          message="Are you sure you want to delete this record?"
        />
      )}

      {isConfirmApproveRejectModalOpen && (
        <ActivityApproveRejectConfirmationModal
          handleClose={handleCloseActivityApproveRejectModal}
          handleConfirm={(approvalStatus, remarks) =>
            handleConfirmApprove(
              roleId,
              selectedActivityId,
              approvalStatus,
              remarks
            )
          }
          roleId={roleId}
          isLoading={loading}
        />
      )}

      {isEnrollModalOpen && (
        <PupilActivityEnrollmentModal
          handleClose={handleCloseEnrollModal}
          selectedActivityId={selectedActivityId}
          selectedActivityType="Event"
          activitiesData={eventActivitiesData}
          nameField="eventName"
          slots={dayAndTimeSlotsData}
        />
      )}

      {loading && <FullScreenLoader />}
    </>
  );
}

export default EventCreationForm;
